/* eslint-disable import/no-deprecated */
import { OmitStrict } from '@fullstory/typeutils';
import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { Popover } from '../../components/Popover';
import { Box } from '../../legacy/Box';
import { Typography } from '../Typography';
import css from './optionMenu.module.scss';

/**
 * @deprecated
 */
export type OptionMenuCategoryProps<T> = {
  categoryFromOption: (op: T) => string;
  // Derive a display label from a search type holding a category.
  categoryToDisplay: (s: string) => string;
  // Category equality is used for rendering and grouping under the labeled category section in the popup menu.
  // options of the same category ought to be pre-sorted otherwise a new label will be inserted when a the next option's category differs from the previous.
  eqCategory: (a: T, B: T) => boolean;

  // lockCategory mean we will hide the category labels since the category options is locked.
  lockCategory: boolean;

  // Color for the category headings, defaulted to "pink" below
  headingColor?: React.ComponentProps<typeof Typography>['color'];
};

/**
 * @deprecated
 */
interface OptionMenuProps<T>
  extends Pick<React.ComponentProps<typeof Popover>, 'anchor' | 'onCloseSignal' | 'placement'> {
  /** Optional classname to append to each option item */
  itemClassName?: string;
  /** The list of options to show in the menu. */
  options: T[];
  /** Function to map an option to a string ID. */
  optionId: (opt: T, index: number) => string;
  /**
   * Function to map an option to a user-facing textual representation.
   */
  optionText: (opt: T) => string;
  /** Optional ID of the currently focused option. */
  focusedOptionId?: string | null;
  /** Callback for when an option is selected. */
  onSelect: (opt: T) => void;
  /** Optional classname. */
  className?: string;
  /** Optional content to render above the list of options. */
  renderAbove?: (onCloseSignal?: () => void) => React.ReactNode;
  /** Optional content to render below the list of options. */
  renderBelow?: (onCloseSignal?: () => void) => React.ReactNode;
  /** Optional content to render each item within the list of options. */
  renderItem?: (opt: T) => React.ReactNode;
  /** Optional content to render search above the list of options. */
  renderSearch?: () => React.ReactNode;
  /** If true, hide the search component. */
  hideSearch?: boolean;
  /** If true, sets the width of the select to match that of the anchor element. */
  matchWidth?: boolean;

  // Optionally adds title attribute to each option menu.
  showTitles?: boolean;

  // Height of the menu before a scroll bar appears. Can be any valid CSS object value.
  maxHeight?: number | string;

  // presence of categoryOps will display category separators between the options of different categories.
  categoryOps?: OptionMenuCategoryProps<T>;
}

/**
 * Convenience type for consumer components to base their props off of.
 * The fields that are likely to be controlled by the consumer class have been omitted.
 * Consumer classes are still free to directly use OptionMenuProps if they need to.
 *
 * @deprecated
 */
type BaseOptionMenuProps<T> = OmitStrict<
  OptionMenuProps<T>,
  'focusedOptionId' | 'anchor' | 'onCloseSignal'
>;

/**
 * A popover menu displaying a list of options for something.
 *
 * @deprecated
 */
class OptionMenu<T> extends React.PureComponent<OptionMenuProps<T>> {
  static defaultProps = {
    maxHeight: 300,
    hideSearch: false,
  };

  /** Ref to the currently focused option. Needed so we can scroll it into view. */
  private focusedRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.scrollFocusedOptionIntoView();
  }

  componentDidUpdate(prevProps: Readonly<OptionMenuProps<T>>) {
    if (this.props.focusedOptionId !== prevProps.focusedOptionId) {
      this.scrollFocusedOptionIntoView();
    }
  }

  setStyle = (): React.CSSProperties => ({
    minWidth: this.props.matchWidth ? this.props.anchor?.clientWidth : undefined,
  });

  render() {
    return (
      <Popover
        data-component="OptionMenu"
        open
        anchor={this.props.anchor}
        placement={this.props.placement}
        onCloseSignal={this.props.onCloseSignal}
        offset={6}
      >
        <div className={classNames(css.optionMenu, this.props.className)}>
          {this.props.renderSearch && (
            <div
              style={this.setStyle()}
              className={classNames(css.content, {
                [css.invisible]: Boolean(this.props.hideSearch),
              })}
            >
              {this.props.renderSearch()}
            </div>
          )}
          {this.props.renderAbove && (
            <div style={this.setStyle()} className={css.content}>
              {this.props.renderAbove()}
            </div>
          )}
          <div
            className={classNames(css.options, 'options')}
            style={{ ...this.setStyle(), maxHeight: this.props.maxHeight }}
          >
            {this.props.options.map((opt, i) => {
              const id = this.props.optionId(opt, i);
              const className = classNames(
                css['optionMenu-item'],
                'optionMenu-item',
                this.props.itemClassName,
                {
                  [css.focus]: this.props.focusedOptionId == id,
                },
              );
              const prevOption = this.props.options[i - 1];
              return (
                <div data-testid="option-menu" key={id}>
                  {this.props.categoryOps &&
                    !this.props.categoryOps.lockCategory &&
                    // Display the category label when the next element to be displayed has a different category than the prior.
                    (i == 0 ||
                    (prevOption && !this.props.categoryOps.eqCategory(prevOption, opt)) ? (
                      <Box padding={[16, 12, 4, 12]}>
                        <Typography
                          variant="heading6"
                          fontWeight="medium"
                          color={this.props.categoryOps.headingColor || 'pink'}
                        >
                          {this.props.categoryOps.categoryToDisplay(
                            this.props.categoryOps.categoryFromOption(opt),
                          )}
                        </Typography>
                      </Box>
                    ) : null)}
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                  <div
                    className={className}
                    ref={id == this.props.focusedOptionId ? this.focusedRef : null}
                    onClick={e => this.props.onSelect(opt)}
                    title={this.props.showTitles ? this.props.optionText(opt) : ''}
                  >
                    {this.props.renderItem && this.props.renderItem(opt)}
                    {!this.props.renderItem && this.props.optionText(opt)}
                  </div>
                </div>
              );
            })}
          </div>
          {this.props.renderBelow && (
            <div className="contentBelow" style={this.setStyle()}>
              {this.props.renderBelow(this.props.onCloseSignal)}
            </div>
          )}
        </div>
      </Popover>
    );
  }

  private scrollFocusedOptionIntoView = () => {
    if (this.focusedRef && this.focusedRef.current) {
      this.focusedRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  };
}

export { OptionMenu };
export type { BaseOptionMenuProps, OptionMenuProps };
